import { START_NODE_RADIUS, STATUS_HEIGHT } from './ellipsis.tsx';
import type { DiagramStatus, DiagramTransition, Line, Point } from './toPortInfo.tsx';
import type { Positionable } from './ListEdgeDetails.tsx';
import type { DragDropState } from './useCanvasDragDropEvents.tsx';
import { getDraggingItem } from './getDraggingItem.tsx';
import type { Bounds } from './VerticalSnappable.types.tsx';
import {
	findAllSnappableLines,
	aggregateSnapLines,
	snapSubject,
	toLines,
	getSubjectBounds,
	getOtherBounds,
	getBounds,
} from './snapSubject.tsx';

export interface Props {
	isImmutable: boolean;
	startNodePosition: Point | undefined;
	globalLoopsContainerPosition: Point | undefined;
	statuses: DiagramStatus[];
	transitions: DiagramTransition[];
	statusWidths: Record<string, number>;
	movingItem: Positionable | undefined;
	dragDropState: DragDropState;
}

export const useSnapLines = ({
	isImmutable,
	statuses,
	transitions,
	statusWidths,
	startNodePosition,
	globalLoopsContainerPosition,
	movingItem,
	dragDropState,
}: Props):
	| {
			snapLines: never[];
			snappedSubjectPosition?: undefined;
	  }
	| {
			snapLines: Line[];
			snappedSubjectPosition: Point;
	  } => {
	const subject = movingItem || getDraggingItem(dragDropState);
	const globalLoopedTransitions = transitions.filter(
		(transition) => transition.type === 'global-looped',
	);

	if (isImmutable || !subject) {
		return { snapLines: [] };
	}

	const subjectBounds = getSubjectBounds(subject, statusWidths, globalLoopedTransitions.length);

	const othersOld: Bounds[] = statuses
		.filter((status) => status.id !== subject.id && status.position)
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		.map((status) => getBounds(status.position!, statusWidths[status.id], STATUS_HEIGHT));

	if (subject.type !== 'start-node' && startNodePosition) {
		othersOld.push(getBounds(startNodePosition, START_NODE_RADIUS * 2, START_NODE_RADIUS * 2));
	}

	const otherBounds = getOtherBounds(
		statuses,
		statusWidths,
		subject,
		startNodePosition,
		globalLoopsContainerPosition,
		globalLoopedTransitions.length,
	);

	const snappables = findAllSnappableLines(subjectBounds, otherBounds);
	const snapLines = aggregateSnapLines(subjectBounds, snappables);
	const snappedSubjectPosition = snapSubject(subjectBounds, snapLines);

	return { snapLines: toLines(snapLines), snappedSubjectPosition };
};
